import { Archive, ArrowLeft, CheckCircle2, ExternalLink, Pencil, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DetailColumns } from '@/components/ui/detail-columns';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { AttachmentsPanel } from '@/features/documents';
import { AuditTimelinePanel } from '@/features/audit';
import { CommentsPanel } from '@/features/comments';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import { useCompany } from '@/features/crm/companies/queries';
import { formatMoney } from '@/lib/money';
import { statusLabel, statusVariant } from '../constants';
import {
  useArchiveContract,
  useContract,
  usePurgeContract,
  useRestoreContract,
  useSignContract,
} from '../queries';
import { SignContractDialog } from '../components/SignContractDialog';

function fmtDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

export function ContractDetailPage() {
  const { publicId = '' } = useParams();
  const contractId = publicId;
  const navigate = useNavigate();

  const { data: contract, isPending, isError, error } = useContract(contractId);
  const company = useCompany(contract?.company ?? -1);
  const archive = useArchiveContract();
  const restore = useRestoreContract();
  const purge = usePurgeContract();
  const sign = useSignContract();
  const [signOpen, setSignOpen] = useState(false);

  async function handleArchive() {
    await archive.mutateAsync(contractId);
    navigate('/crm/contracts');
  }

  function handleRestore() {
    restore.mutate(contractId);
  }

  function handlePurge() {
    purge.mutate(contractId, { onSuccess: () => navigate('/crm/contracts') });
  }

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/crm/contracts')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  const canSign = contract.status !== 'signed' && contract.status !== 'cancelled';

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/crm/contracts')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{contract.title}</h1>
          <Badge variant={statusVariant(contract.status)}>{statusLabel(contract.status)}</Badge>
        </div>
        <div className="flex gap-2">
          {canSign ? (
            <Button onClick={() => setSignOpen(true)} disabled={sign.isPending}>
              <CheckCircle2 /> {sign.isPending ? 'Signing…' : 'Sign'}
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => navigate(`/crm/contracts/${contract.public_id}/edit`)}>
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" aria-label="Archive contract">
                <Archive /> Archive
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archive this contract?</AlertDialogTitle>
                <AlertDialogDescription>
                  Hide this row from the active list. You can restore it later from Archived view.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleArchive} disabled={archive.isPending}>
                  {archive.isPending ? 'Archiving…' : 'Archive'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {contract.is_archived ? (
        <Card className="bg-muted/40 border-dashed">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
            <div className="text-sm">
              <p className="font-medium">This contract is archived.</p>
              <p className="text-muted-foreground">
                Restore it to make it visible in the active list again.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleRestore}
                disabled={restore.isPending}
                aria-label="Restore contract"
              >
                <RotateCcw /> {restore.isPending ? 'Restoring…' : 'Restore'}
              </Button>
              <PurgeArchivedButton
                variant="full"
                name={contract.title}
                onPurge={handlePurge}
                pending={purge.isPending}
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      <DetailColumns
        main={
          <>
            <Card>
              <CardHeader>
                <CardTitle>Summary</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase">Company</p>
                  <p className="text-foreground mt-1">{company.data?.name ?? '…'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase">Owner</p>
                  <p className="text-foreground mt-1">{contract.owner_name ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase">
                    Amount ({contract.currency ?? '—'})
                  </p>
                  <p className="text-foreground mt-1 tabular-nums">
                    {contract.amount == null
                      ? 'Restricted'
                      : (formatMoney(contract.amount, contract.currency) ?? '—')}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase">Amount (USD base)</p>
                  <p className="text-foreground mt-1 font-semibold tabular-nums">
                    {contract.base_amount == null
                      ? 'Restricted'
                      : (formatMoney(contract.base_amount, 'USD') ?? '—')}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase">Term</p>
                  <p className="text-foreground mt-1">
                    {fmtDate(contract.starts_on)} → {fmtDate(contract.ends_on)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase">Signed</p>
                  <p className="text-foreground mt-1">
                    {contract.signed_at ? new Date(contract.signed_at).toLocaleString() : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase">Project</p>
                  <p className="text-foreground mt-1">
                    {contract.project ? (
                      <Button
                        variant="link"
                        className="h-auto p-0"
                        onClick={() => navigate(`/projects/${contract.project}`)}
                      >
                        Open project <ExternalLink className="size-3" />
                      </Button>
                    ) : (
                      '—'
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>

            {contract.notes ? (
              <Card>
                <CardHeader>
                  <CardTitle>Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">{contract.notes}</p>
                </CardContent>
              </Card>
            ) : null}

            <AttachmentsPanel modelLabel="crm.contract" objectId={contract.id} />

            <CommentsPanel modelLabel="crm.contract" objectId={contract.id} />
          </>
        }
        aside={
          <>
            <AuditTimelinePanel entity="crm.contract" entityId={contract.id} />
          </>
        }
      />
      {canSign ? (
        <SignContractDialog
          open={signOpen}
          onOpenChange={setSignOpen}
          contract={contract}
          onSigned={(projectId) => navigate(`/projects/${projectId}`)}
        />
      ) : null}
    </div>
  );
}
